import { ChangeDetectionStrategy, Component, Input } from '@angular/core'

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { TranslateModule } from '@ngx-translate/core'
import { ItemTableCardType } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterExcludedItemsDialogComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-excluded-items-dialog/gb-presenter-excluded-items-dialog.component'
import { GbPresenterItemFilterDialogComponent } from 'libs/gb-dashboard/presenter/feature/src/lib/gb-presenter-item-filter-dialog/gb-presenter-item-filter-dialog.component'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { GbTokenColorPickerComponent } from 'libs/gb-dashboard/shared/ui/src/lib/gb-token-color-picker/gb-token-color-picker.component'
import { IconLibrary } from 'libs/gb-dashboard/shared/ui/src/lib/gb-icon/gb-icon-library.models'
import { GbPresenterToolsTextComponent } from '../gb-presenter-tools-text/gb-presenter-tools-text.component'
import { GbPresenterToolsAdvancedOptionsContainerComponent } from '../gb-presenter-tools-advanced-options-container/gb-presenter-tools-advanced-options-container.component'

@Component({
  selector: 'gb-presenter-tools-item-table',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
    GbDashboardSharedUiModule,
    GbTokenColorPickerComponent,
    GbPresenterToolsTextComponent,
    GbPresenterToolsAdvancedOptionsContainerComponent,
  ],
  templateUrl: './gb-presenter-tools-item-table.component.html',
  styleUrl: './gb-presenter-tools-item-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterToolsItemTableComponent {
  @Input({ required: true }) toolsFormGroup!: FormGroup

  hideFields = ['font_weight', 'font_style', 'text_align', 'color']

  IconLibrary = IconLibrary

  private readonly listPresetDefaults = {
    columns: 1,
    rows: 5,
    slide_interval: 10,
    font_size: 14,
    card_type: ItemTableCardType.STANDARD_TABLE,
    height: 85,
    width: 99,
  }

  private readonly gridPresetDefaults = {
    columns: 5,
    rows: 1,
    slide_interval: 10,
    font_size: 12,
    card_type: ItemTableCardType.COMPACT_TABLE,
    height: 50,
    width: 99,
  }

  cardTypeOptions = [
    { value: ItemTableCardType.STANDARD_TABLE, label: 'PRESENTER.ITEM_TABLE_TOOLS.CARD_TYPES.STANDARD_TABLE' },
    { value: ItemTableCardType.COMPACT_TABLE, label: 'PRESENTER.ITEM_TABLE_TOOLS.CARD_TYPES.COMPACT_TABLE' },
    { value: ItemTableCardType.TALL_CARD, label: 'PRESENTER.ITEM_TABLE_TOOLS.CARD_TYPES.TALL_CARD' },
  ]

  transitionOptions = [
    { value: 'fade-in', label: 'PRESENTER.ITEM_TABLE_TOOLS.TRANSITIONS.FADE_IN' },
    { value: 'scroll-up', label: 'PRESENTER.ITEM_TABLE_TOOLS.TRANSITIONS.SCROLL_UP' },
    { value: 'scroll-down', label: 'PRESENTER.ITEM_TABLE_TOOLS.TRANSITIONS.SCROLL_DOWN' },
    { value: 'scroll-left', label: 'PRESENTER.ITEM_TABLE_TOOLS.TRANSITIONS.SCROLL_LEFT' },
    { value: 'scroll-right', label: 'PRESENTER.ITEM_TABLE_TOOLS.TRANSITIONS.SCROLL_RIGHT' },
  ]

  constructor(
    private gbDashboardDialogService: GbDashboardDialogService,
    private presenterContextService: PresenterContextService,
  ) {}

  get isTemplateMode(): boolean {
    return this.presenterContextService.isTemplateMode
  }

  openFilterItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    const dialogRef = this.gbDashboardDialogService.openDialog(GbPresenterItemFilterDialogComponent, {
      width: '750px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return

      this.hiddenItemsControl.setValue(result.hiddenItemUuids)
      this.hiddenItemsControl.markAsDirty()
      this.toolsFormGroup.markAsDirty()
    })
  }

  isPresetActive(preset: 'list' | 'grid'): boolean {
    if (!this.toolsFormGroup) return false

    const values = preset === 'list' ? this.listPresetDefaults : this.gridPresetDefaults

    return (
      this.columnsControl?.value === values.columns &&
      this.rowsControl?.value === values.rows &&
      this.cardTypeControl?.value === values.card_type
    )
  }

  applyPreset(preset: 'list' | 'grid'): void {
    const values = preset === 'list' ? this.listPresetDefaults : this.gridPresetDefaults
    this.toolsFormGroup.patchValue(values)
    this.toolsFormGroup.markAsDirty()
  }

  get columnsControl(): FormControl {
    return this.toolsFormGroup.get('columns') as FormControl
  }

  get rowsControl(): FormControl {
    return this.toolsFormGroup.get('rows') as FormControl
  }

  get slideIntervalControl(): FormControl {
    return this.toolsFormGroup.get('slide_interval') as FormControl
  }

  get cardTypeControl(): FormControl {
    return this.toolsFormGroup.get('card_type') as FormControl
  }

  get transitionControl(): FormControl {
    return this.toolsFormGroup.get('transition') as FormControl
  }

  get imageContainedControl(): FormControl {
    return this.toolsFormGroup.get('image_contained') as FormControl
  }

  get showItemTypeControl(): FormControl {
    return this.toolsFormGroup.get('show_item_type') as FormControl
  }

  get itemNameMaxLinesControl(): FormControl {
    return this.toolsFormGroup.get('item_name_max_lines') as FormControl
  }

  get tablePrimaryColorControl(): FormControl {
    return this.toolsFormGroup.get('table_primary_color') as FormControl
  }

  get tableAccentColorControl(): FormControl {
    return this.toolsFormGroup.get('table_accent_color') as FormControl
  }

  get tableBackgroundColorControl(): FormControl {
    return this.toolsFormGroup.get('table_background_color') as FormControl
  }

  get hiddenItemsControl(): FormControl {
    return this.toolsFormGroup.get('hidden_items') as FormControl
  }

  get excludedItemsCount(): number {
    return ((this.hiddenItemsControl?.value || []) as string[]).length
  }

  openExcludedItemsDialog(): void {
    const hiddenItemUuids = (this.hiddenItemsControl?.value || []) as string[]

    this.gbDashboardDialogService.openDialog(GbPresenterExcludedItemsDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      data: { hiddenItemUuids },
    })
  }
}
